import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  FormControl,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { MatOptionModule } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";

/** Objet générique clé/valeur pour rendre le composant réutilisable */
type GenericItem = Record<string, any>;

@Component({
  selector: "app-autocomplete-editable",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDataComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./autocomplete.component.html",
  styleUrls: ["./autocomplete.component.scss"],
})
export class AutocompleteDataComponent
  implements ControlValueAccessor, AfterViewInit
{
  /** Liste d’objets à afficher (générique) */
  @Input({ required: true }) items: GenericItem[] = [];

  /** Clé à afficher (par défaut "code", sinon "name", ou celle que tu veux) */
  @Input() displayKey: keyof GenericItem | string = "code";

  /** Clés utilisées par la recherche dans le panel */
  @Input() searchKeys: (keyof GenericItem | string)[] = ["code", "name"];

  /** Apparence & libellé */
  @Input() label = "Avion";
  @Input() placeholder = "";
  @Input() appearance: "legacy" | "standard" | "fill" | "outline" = "outline";
  @Input() autoActiveFirstOption = false;
  @Input() autocompleteAttr: "off" | "on" = "off";

  /** Événements optionnels */
  @Output() selectionChange = new EventEmitter<GenericItem>();
  @Output() cleared = new EventEmitter<void>();

  @ViewChild("panelSearch", { read: ElementRef })
  panelSearchEl?: ElementRef<HTMLInputElement>;
  @ViewChild("hostInput", { read: ElementRef })
  hostInputEl?: ElementRef<HTMLInputElement>;
  // Triggers
  @ViewChild("hostInput", { read: MatAutocompleteTrigger })
  trigger?: MatAutocompleteTrigger;

  readonly displayCtrl = new FormControl<string>("", { nonNullable: true });
  readonly panelSearchCtrl = new FormControl<string>("", { nonNullable: true });

  ngAfterViewInit(): void {
    // 1) A l'ouverture: focus robuste sur l'input de recherche + "défocus" de l'input principal
    this.bindOpenedFocus();

    // 2) Si l'input principal reprend le focus alors que le panel est ouvert → renvoyer le focus au search
    this.redirectFocusWhileOpen();

    // 3) Redirection de frappe (option utile si l'utilisateur tape trop vite)
    this.attachTypeRedirection();
  }

  /** Valeur réelle (l’objet sélectionné) */
  value: GenericItem | null = null;

  // ---- ControlValueAccessor ----
  private onChange: (v: GenericItem | null) => void = () => {};
  onTouched: () => void = () => {};
  writeValue(v: GenericItem | null): void {
    this.value = v;
    this.displayCtrl.setValue(this.optionLabel(v), { emitEvent: false });
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    disabled
      ? (this.displayCtrl.disable({ emitEvent: false }),
        this.panelSearchCtrl.disable({ emitEvent: false }))
      : (this.displayCtrl.enable({ emitEvent: false }),
        this.panelSearchCtrl.enable({ emitEvent: false }));
  }
  // --------------------------------

  /** Texte à afficher pour un item (sans besoin d’une fonction parent) */
  optionLabel = (item: GenericItem | null | undefined): string => {
    if (!item) return "";
    const key = this.displayKey as string;
    const val = item[key];
    // fallbacks intelligents si displayKey n'existe pas
    return typeof val === "string" && val
      ? val
      : (
          item.code ??
          item.name ??
          item.label ??
          item.username ??
          item.id ??
          ""
        ).toString();
  };

  filtered(): GenericItem[] {
    const q = (this.panelSearchCtrl.value || "").trim().toLowerCase();
    if (!q) return this.items;
    const keys = this.searchKeys.map((k) => k.toString());
    return this.items.filter((obj) =>
      keys.some((k) => {
        const v = obj?.[k];
        return typeof v === "string" && v.toLowerCase().includes(q);
      })
    );
  }

  focusSearchBox(): void {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.panelSearchEl?.nativeElement?.focus({ preventScroll: true });
        this.hostInputEl?.nativeElement?.blur();
        try {
          const el = this.panelSearchEl?.nativeElement;
          if (el) el.setSelectionRange(el.value.length, el.value.length);
        } catch {}
      })
    );
  }

  resetSearch(): void {
    this.panelSearchCtrl.setValue("", { emitEvent: false });
  }

  onOptionSelected(ev: MatAutocompleteSelectedEvent): void {
    const item = ev.option.value as GenericItem | null;
    if (!item) return;
    this.value = item;
    this.displayCtrl.setValue(this.optionLabel(item));
    this.onChange(item);
    this.selectionChange.emit(item);
  }

  clearSelection(e?: Event): void {
    e?.stopPropagation();
    this.value = null;
    this.displayCtrl.setValue("");
    this.onChange(null);
    this.cleared.emit();
  }

  /** Force le focus dans l'input de recherche à l'ouverture, et sort le focus de l'input principal. */
  private bindOpenedFocus(): void {
    const trigger = this.trigger;
    if (!trigger) return;

    const searchEl = this.panelSearchEl?.nativeElement;
    const host = this.hostInputEl?.nativeElement;

    trigger.autocomplete?.opened.subscribe(() => {
      // double rAF pour passer après l’animation interne
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          searchEl?.focus();
          // IMPORTANT : sortir le focus de l'input principal pour éviter le "retour"
          host?.blur();
        })
      );
    });
  }

  /** Si le focus revient sur l'input principal pendant que le panel est ouvert, on le renvoie au search. */
  private redirectFocusWhileOpen(): void {
    const host = this.hostInputEl?.nativeElement;
    const trigger = this.trigger;
    const searchEl = this.panelSearchEl?.nativeElement;
    if (!host || !trigger || !searchEl) return;

    host.addEventListener(
      "focus",
      () => {
        if (trigger.panelOpen) {
          // remet immédiatement le focus dans l'input de recherche du panel
          setTimeout(() => searchEl.focus(), 0);
        }
      },
      true // capture → on passe avant tout handler interne
    );
  }

  // Rediriger la 1ère frappe vers la barre de recherche et y injecter la touche
  private attachTypeRedirection(): void {
    const host = this.hostInputEl?.nativeElement;
    const trigger = this.trigger;
    const ctrl = this.panelSearchCtrl;
    const ref = this.panelSearchEl;
    if (!host || !trigger || !ctrl || !ref) return;

    host.addEventListener("keydown", (evt: KeyboardEvent) => {
      if (!trigger.panelOpen) return;

      const isChar =
        evt.key.length === 1 && !evt.ctrlKey && !evt.metaKey && !evt.altKey;
      const isBackspace = evt.key === "Backspace";
      if (!isChar && !isBackspace) return;

      evt.preventDefault();
      ref.nativeElement.focus();

      const val = ctrl.value ?? "";
      ctrl.setValue(isBackspace ? val.slice(0, -1) : val + evt.key);
    });
  }
}
